import classNames from "classnames";
import { useEffect, useRef } from "react";
import { ColumnDefinition } from "../Table.definition";
import styles from "../Table.module.scss";
import { TableSelect } from "./TableSelect";
import { TableSelectionState } from "./useTableSelection";

type TableHeaderProps<T> = {
  columns: ColumnDefinition<T>[];
  renderSelectionActions?: (selectedRows: T[]) => React.ReactElement;
  selectionState: TableSelectionState<T> | null;
  onSort?: (key: string) => void;
};

export function TableHeader<T>({
  columns,
  selectionState,
  renderSelectionActions,
  onSort,
}: TableHeaderProps<T>) {
  const handleKeyDown = (event: React.KeyboardEvent, key: string) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onSort?.(key);
    }
  };
  const actionsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (actionsRef.current) {
      actionsRef.current.style.setProperty(
        "--span-columns",
        String(columns.length)
      );
    }
  }, [columns.length]);

  return (
    <>
      {selectionState !== null && (
        <div
          className={classNames(styles.tableHeader, {
            [styles.firstHeaderCell]: true,
            [styles.lastHeaderCell]: columns.length === 0,
          })}
        >
          <div className={styles.cellInner}>
            <TableSelect
              selected={selectionState.allSelected}
              name="select-all"
              onSelect={selectionState.toggleAll}
            />
          </div>
        </div>
      )}
      {renderSelectionActions && selectionState?.atLeastOneSelected && (
        <div
          ref={actionsRef}
          className={classNames(styles.tableHeader, styles.actionsHeader)}
        >
          {renderSelectionActions(selectionState.selectedRows)}
        </div>
      )}
      {(selectionState === null || !selectionState.atLeastOneSelected) &&
        columns.map((column, index) => (
          <div
            key={column.key}
            tabIndex={0}
            role="button"
            onClick={() => onSort?.(column.key)}
            onKeyDown={(e) => handleKeyDown(e, column.key)}
            className={classNames(styles.tableHeader, {
              [styles.firstHeaderCell]: selectionState === null && index === 0,
              [styles.lastHeaderCell]:
                index + (selectionState !== null ? 1 : 0) ===
                columns.length - 1,
            })}
          >
            {column.title}
            {column.icon}
          </div>
        ))}
    </>
  );
}
